"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useToast } from "@/lib/toast";

export interface CartBundleCourse {
  id: string;
  title: string;
  price: number;
}

export interface CartItem {
  id: string;
  slug: string;
  title: string;
  price: number;
  originalPrice: number;
  thumbnail: string;
  cardColor: string;
  promoEndsAt?: string | null;
  kind?: "course" | "material" | "bundle";
  // Pola zestawu (kind === "bundle"):
  bundleId?: string;
  bundleCourses?: CartBundleCourse[];
  bundleMaterials?: CartBundleCourse[];
  giftable?: boolean;
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: CartItem) => void;
  addBundle: (item: CartItem) => void;
  removeItem: (id: string) => void;
  hasItem: (id: string) => boolean;
  hasCourse: (courseId: string) => boolean;
  clearCart: () => void;
  totalPrice: number;
  totalOriginalPrice: number;
  count: number;
}

const CartContext = createContext<CartContextType | null>(null);

const STORAGE_KEY = "nextschool-cart";

function loadCart(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { toast } = useToast();

  // Load from localStorage on mount
  useEffect(() => {
    setItems(loadCart());
    setLoaded(true);
  }, []);

  // Save whenever items change (after initial load)
  useEffect(() => {
    if (loaded) saveCart(items);
  }, [items, loaded]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((v) => !v), []);

  const addItem = useCallback(
    (item: CartItem) => {
      let added = false;
      let blockedByBundle = false;
      setItems((prev) => {
        if (prev.some((i) => i.id === item.id)) return prev;
        // Kurs już objęty zestawem w koszyku — nie dodawaj pojedynczo
        // (uniknięcie podwójnego naliczenia).
        if (
          item.kind !== "bundle" &&
          prev.some(
            (i) =>
              i.kind === "bundle" &&
              (i.bundleCourses?.some((c) => c.id === item.id) ||
                i.bundleMaterials?.some((m) => m.id === item.id))
          )
        ) {
          blockedByBundle = true;
          return prev;
        }
        added = true;
        return [...prev, item];
      });
      if (blockedByBundle) {
        toast({
          variant: "info",
          title: item.title,
          description: "Ten kurs jest już w koszyku w ramach zestawu",
        });
        return;
      }
      // Powiadomienie o dodaniu do koszyka (zamiast wymuszonego otwierania
      // panelu) — z w pełni klikalnym przyciskiem przejścia do koszyka.
      if (added) {
        toast({
          variant: "success",
          title: item.title,
          description: "Kurs został dodany do koszyka",
          action: { label: "Przejdź do koszyka", onClick: () => setIsOpen(true) },
        });
      }
    },
    [toast]
  );

  // Dodanie zestawu: usuwa z koszyka pojedyncze kursy wchodzące w jego skład
  // (aby nie naliczyć ich podwójnie), a następnie dodaje kafelek zestawu.
  const addBundle = useCallback(
    (item: CartItem) => {
      let added = false;
      const coveredIds = new Set([
        ...(item.bundleCourses ?? []).map((c) => c.id),
        ...(item.bundleMaterials ?? []).map((m) => m.id),
      ]);
      setItems((prev) => {
        if (prev.some((i) => i.id === item.id)) return prev;
        added = true;
        const withoutOverlap = prev.filter(
          (i) => !(i.kind !== "bundle" && coveredIds.has(i.id))
        );
        return [...withoutOverlap, item];
      });
      if (added) {
        toast({
          variant: "success",
          title: item.title,
          description: "Zestaw został dodany do koszyka",
          action: { label: "Przejdź do koszyka", onClick: () => setIsOpen(true) },
        });
      }
    },
    [toast]
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const hasItem = useCallback(
    (id: string) => items.some((i) => i.id === id),
    [items]
  );

  // Czy kurs jest w koszyku — pojedynczo LUB w ramach zestawu.
  const hasCourse = useCallback(
    (courseId: string) =>
      items.some(
        (i) =>
          i.id === courseId ||
          (i.kind === "bundle" && !!i.bundleCourses?.some((c) => c.id === courseId))
      ),
    [items]
  );

  const clearCart = useCallback(() => setItems([]), []);

  const totalPrice = items.reduce((sum, i) => sum + i.price, 0);
  const totalOriginalPrice = items.reduce((sum, i) => sum + (i.originalPrice || i.price), 0);
  const count = items.length;

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        openCart,
        closeCart,
        toggleCart,
        addItem,
        addBundle,
        removeItem,
        hasItem,
        hasCourse,
        clearCart,
        totalPrice,
        totalOriginalPrice,
        count,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
